import {
  DefaultNodeTypes,
  SerializedBlockNode,
  SerializedLinkNode,
  SerializedInlineBlockNode,
  type DefaultTypedEditorState,
} from '@payloadcms/richtext-lexical'
import {
  JSXConvertersFunction,
  LinkJSXConverter,
  RichText as ConvertRichText,
} from '@payloadcms/richtext-lexical/react'

import { vimeoUrlToEmbedSrc } from '@/payload/blocks/VimeoEmbed/utils'
import { TenantContactHref } from '@/components/ui/TenantContactInfo'
import { TenantName } from '@/components/ui/TenantName'
import { cn } from '@/utilities/ui'

type NodeTypes = DefaultNodeTypes | SerializedBlockNode | SerializedInlineBlockNode

const internalDocToHref = ({ linkNode }: { linkNode: SerializedLinkNode }) => {
  const { value } = linkNode.fields.doc!
  if (typeof value !== 'object') {
    throw new TypeError('Expected value to be an object')
  }
  const slug = value.slug
  return `/${slug}`
}

const jsxConverters: JSXConvertersFunction<NodeTypes> = ({ defaultConverters }) => ({
  ...defaultConverters,
  ...LinkJSXConverter({ internalDocToHref }),
  blocks: {
    vimeoEmbed: ({ node }: { node?: { fields?: { url?: string } } }) => {
      const url: string | undefined = node?.fields?.url
      if (typeof url !== 'string' || !url) return null

      const src = vimeoUrlToEmbedSrc(url)
      if (!src) return null

      return (
        <iframe
          data-testid="video-frame"
          src={src}
          width="100%"
          style={{ aspectRatio: '16/9' }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="Embedded Vimeo video"
        />
      )
    },
  },
  inlineBlocks: {
    tenantContactInfo: () => {
      return <TenantContactHref />
    },
    tenantName: () => {
      return <TenantName />
    },
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  'custom-heading': ({ node, nodesToJSX }: any) => {
    const children = nodesToJSX({ nodes: node.children })
    const style: React.CSSProperties = {}
    const tag = node.tag || 'h1'

    // Parse inline styles for background and text color
    const bgMatch = node?.style?.match(/background-color:\s*([^;]+)/)
    const colorMatch = node?.style?.match(/(?:^|;)\s*color:\s*([^;]+)/)

    if (bgMatch) style.backgroundColor = bgMatch[1]
    if (colorMatch) style.color = colorMatch[1]

    // Add padding if there's a background color for better visual appearance
    if (bgMatch) {
      style.paddingTop = '.5rem'
      style.paddingBottom = '.5rem'
    }

    // Render only valid heading tags (h1-h6) or fallback to h2
    const allowedHeadings = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const
    const Tag = (allowedHeadings.includes(tag) ? tag : 'h1') as React.ElementType
    return Object.keys(style).length ? <Tag style={style}>{children}</Tag> : <Tag>{children}</Tag>
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  'custom-paragraph': ({ node, nodesToJSX }: any) => {
    const children = nodesToJSX({ nodes: node.children })
    const bgMatch = node?.style?.match(/background-color:\s*([^;]+)/)
    const style: React.CSSProperties | undefined = bgMatch
      ? { backgroundColor: bgMatch[1] }
      : undefined
    return children?.length ? (
      <p style={style}>{children}</p>
    ) : (
      <p>
        <br />
      </p>
    )
  },
  text: (args: { node?: { style?: string } }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const base = (defaultConverters as any).text?.(args)
    const style: React.CSSProperties = {}
    const styleStr: string = args?.node?.style || ''
    const bg = /(?:^|;)\s*background-color:\s*([^;]+)/.exec(styleStr)?.[1]
    const color = /(?:^|;)\s*color:\s*([^;]+)/.exec(styleStr)?.[1]
    if (bg) style.backgroundColor = bg
    if (color) style.color = color
    return bg || color ? <span style={style}>{base}</span> : base
  },
})

type Props = {
  data: DefaultTypedEditorState
  enableGutter?: boolean
  enableProse?: boolean
} & React.HTMLAttributes<HTMLDivElement>

export default function RichText(props: Props) {
  const { className, enableProse = false, enableGutter = true, ...rest } = props
  return (
    <ConvertRichText
      converters={jsxConverters}
      className={cn(
        'payload-richtext',
        {
          container: enableGutter,
          'max-w-none': !enableGutter,
          'mx-auto prose md:prose-md': enableProse,
        },
        className,
      )}
      {...rest}
    />
  )
}
